import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { UserContext } from '../context/UserContext';
import { NotificationContext } from '../context/NotificationContext';
import { BASE_URL } from '../utils/config';
import { apiFetch } from '../utils/apiClient';
import './ImpersonationBanner.css';
import { jwtDecode } from 'jwt-decode';

const ImpersonationBanner = () => {
  const { user, updateUser, updateToken } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useLanguage();

  const handleReturnToAdmin = async () => {
    const adminRefreshToken = localStorage.getItem('adminRefreshToken');
    if (!adminRefreshToken) {
      addNotification(t('impersonationBanner.noAdminSessionError'), 'error');
      return;
    }

    try {
      // Step 1: Use the admin refresh token to get a new access token
      const refreshResponse = await fetch(`${BASE_URL}/authUser/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: adminRefreshToken }),
      });

      if (!refreshResponse.ok) {
        throw new Error(t('impersonationBanner.refreshSessionError'));
      }

      const { accessToken: newAdminAccessToken } = await refreshResponse.json();

      // Step 2: Decode the new token to get the admin's ID
      const decodedAdmin = jwtDecode(newAdminAccessToken);
      const adminId = decodedAdmin.userId;

      // Step 3: Fetch the full admin user object
      const userDetailsResponse = await apiFetch(`${BASE_URL}/authUser/users/${adminId}`, {}, newAdminAccessToken);
      if (!userDetailsResponse.ok) {
        throw new Error(t('impersonationBanner.fetchAdminDetailsError'));
      }
      const adminDetails = await userDetailsResponse.json();

      // Step 4: Restore tokens in localStorage
      localStorage.setItem('refreshToken', adminRefreshToken);
      localStorage.removeItem('adminRefreshToken');

      // Step 5: Update the user context
      updateToken(newAdminAccessToken);
      updateUser({
        isLoggedIn: true,
        token: newAdminAccessToken,
        id: adminDetails._id,
        fullName: `${adminDetails.firstName} ${adminDetails.lastName}`,
        username: adminDetails.username,
        role: adminDetails.role,
        email: adminDetails.email,
        creditPoints: adminDetails.creditPoints,
        savedProperties: adminDetails.savedProperties || [],
        preferences: adminDetails.preferences,
        // Restore verification status
        isEmailVerified: adminDetails.isEmailVerified,
        isPhoneVerified: adminDetails.isPhoneVerified,
        isFullyVerified: adminDetails.isFullyVerified,
        impersonatorId: null, // Clear impersonatorId
      });

      addNotification(t('impersonationBanner.returnSuccess'), 'success');
      navigate(`/${lang}/admin/user-management`);
    } catch (error) {
      console.error('Error returning to admin:', error);
      addNotification(t('impersonationBanner.returnError', { message: error.message }), 'error');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('adminRefreshToken');
      updateUser({ isLoggedIn: false, role: 'Guest' });
      navigate(`/${lang}/login`);
    }
  };

  return (
    <div className="impersonation-banner">
      <p>
        <Trans i18nKey="impersonationBanner.viewingAs" values={{ username: user.username }}>
          You are currently viewing as <strong>{{ username: user.username }}</strong>.
        </Trans>
      </p>
      <button onClick={handleReturnToAdmin}>{t('impersonationBanner.returnButton')}</button>
    </div>
  );
};

export default ImpersonationBanner;
